import React, { useState, useEffect, useRef } from 'react';
import { productAPI, categoryAPI } from '../../api';
import { Plus, Search, Edit, Trash2, X, Upload, Star, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import { AdjustStockModal } from './AdminInventory';
import ImageUpload from '../../components/common/ImageUpload';

const EMPTY_FORM = {
  name: '', description: '', price: '', mrp: '', category_id: '', stock: '',
  images: [], brand: '', tags: '', is_active: true, external_url: '',
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [adjustProduct, setAdjustProduct] = useState(null);
  const limit = 10;

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await productAPI.getAll({ search, page, limit, admin: true });
      setProducts(res.data.products || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, [search, page]);

  useEffect(() => {
    categoryAPI.getAll().then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditProduct(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    setForm({ name: p.name, description: p.description || '', price: p.price, mrp: p.mrp || '', category_id: p.category_id || '', stock: p.stock || 0, images: p.images || [], brand: p.brand || '', tags: (p.tags || []).join(', '), is_active: p.is_active, external_url: p.external_url || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Name and price are required');
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), mrp: form.mrp ? parseFloat(form.mrp) : undefined, tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [] };
      if (!editProduct) payload.stock = parseInt(form.stock) || 0;
      else delete payload.stock;
      if (editProduct) {
        await productAPI.update(editProduct.id, payload);
        toast.success('Product updated');
      } else {
        await productAPI.create(payload);
        toast.success('Product created');
      }
      setShowModal(false);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    setDeleting(id);
    try {
      await productAPI.delete(id);
      toast.success('Product deleted');
      fetchProducts();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Products</h1>
          <p style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{total} products total</p>
        </div>
        <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f43397', color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          <Plus size={18} /> Add Product
        </button>
      </div>

      {/* Search */}
      <div style={{ background: 'white', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Search size={18} color="#94a3b8" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..." style={{ border: 'none', outline: 'none', fontSize: 14, flex: 1, color: '#0f172a' }} />
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Product', 'Category', 'Price', 'Stock', 'Rating', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No products found</td></tr>
                ) : products.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src={p.images?.[0] || `https://picsum.photos/seed/${p.id}/48/48`} alt={p.name} style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.brand || 'No brand'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{p.category_name || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#f43397' }}>₹{parseFloat(p.price).toFixed(0)}</div>
                      {p.mrp && <div style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'line-through' }}>₹{parseFloat(p.mrp).toFixed(0)}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ color: p.stock < 10 ? '#ef4444' : '#22c55e', fontWeight: 600, fontSize: 14 }}>{p.stock}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Star size={14} fill="#f59e0b" color="#f59e0b" />
                        <span style={{ fontSize: 13 }}>{parseFloat(p.avg_rating || 0).toFixed(1)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: p.is_active ? '#dcfce7' : '#fee2e2', color: p.is_active ? '#16a34a' : '#dc2626', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => openEdit(p)} style={{ background: '#ede9fe', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#7c3aed' }}><Edit size={15} /></button>
                        <button onClick={() => handleDelete(p.id)} disabled={deleting === p.id} style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#ef4444' }}>
                          {deleting === p.id ? '...' : <Trash2 size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6, justifyContent: 'center' }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: page === p ? '#f43397' : '#f1f5f9', color: page === p ? 'white' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color="#64748b" /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Product Image</label>
                <ImageUpload value={form.images?.[0]} onChange={url => setForm(f => ({ ...f, images: [url] }))} />
              </div>
              {[
                { label: 'Product Name *', key: 'name', type: 'text' },
                { label: 'Brand', key: 'brand', type: 'text' },
                { label: 'Price (₹) *', key: 'price', type: 'number' },
                { label: 'MRP (₹)', key: 'mrp', type: 'number' },
                ...(editProduct ? [] : [{ label: 'Initial Stock', key: 'stock', type: 'number' }]),
                { label: 'Tags (comma separated)', key: 'tags', type: 'text' },
                { label: 'External Purchase Link (Amazon/Flipkart/etc.)', key: 'external_url', type: 'url' },
              ].map(({ label, key, type }) => (
                <div key={key}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              {editProduct && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 13, color: '#374151' }}>Current stock: <strong>{editProduct.stock}</strong></span>
                  <button type="button" onClick={() => setAdjustProduct(editProduct)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#ede9fe', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#7c3aed', fontSize: 13, fontWeight: 600 }}>
                    <SlidersHorizontal size={14} /> Adjust Stock
                  </button>
                </div>
              )}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Category</label>
                <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label htmlFor="is_active" style={{ fontSize: 14, color: '#374151' }}>Active (visible to customers)</label>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#f43397', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : (editProduct ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {adjustProduct && (
        <AdjustStockModal
          product={adjustProduct}
          onClose={() => setAdjustProduct(null)}
          onSaved={(updated) => {
            setAdjustProduct(null);
            if (updated) setEditProduct(ep => (ep && ep.id === updated.id ? updated : ep));
            fetchProducts();
          }}
        />
      )}
    </div>
  );
}
